import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, GitBranch, Radar, AlertTriangle, ScanLine, BellRing,
  ArrowRight, Lock, Workflow, CheckCircle2, Activity, Mail, Globe,
  Building2, Briefcase, Users, Stethoscope, ShoppingBag, Landmark, TrendingUp,
} from "lucide-react";
import TelemetryHero from "@/components/marketing/TelemetryHero";

const monitorCards = [
  { icon: ShieldCheck, label: "DMARC", desc: "Aggregate report ingestion, alignment monitoring, enforcement readiness." },
  { icon: GitBranch, label: "SPF", desc: "Lookup depth analysis, include-chain explosion detection, flattening guidance." },
  { icon: Lock, label: "DKIM", desc: "Key rotation drift, signature validation, alignment telemetry." },
  { icon: ScanLine, label: "BIMI", desc: "Logo validation, VMC readiness, brand trust signals." },
  { icon: Radar, label: "Sender Discovery", desc: "Map every SaaS and unknown sender using your domain." },
  { icon: AlertTriangle, label: "Threat Intel", desc: "Spoofing spikes, suspicious infrastructure, auth anomalies." },
  { icon: Activity, label: "Deliverability", desc: "Reputation drift, blocklist exposure, alignment health." },
  { icon: Workflow, label: "Enforcement", desc: "Confidence scoring for p=quarantine and p=reject moves." },
  { icon: Lock, label: "SSL / TLS Posture", desc: "MTA-STS, TLS-RPT, DANE, and MX certificate expiry monitoring." },
  { icon: Globe, label: "Domain Reputation", desc: "Blocklist exposure, Google Postmaster, sender reputation scoring." },
];

const pipeline = [
  { label: "Ingestion", desc: "DMARC reports routed globally to edge workers." },
  { label: "DNS Validation", desc: "SPF chains, DKIM selectors, BIMI assets validated." },
  { label: "Policy Evaluation", desc: "Alignment scoring and enforcement modeling." },
  { label: "Threat Detection", desc: "Spoofing spikes and suspicious IP graphing." },
  { label: "Alerting", desc: "Slack, Teams, email — tenant-scoped only." },
];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative pt-20 pb-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-8" data-testid="hero-eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-500"></span>
                <span className="text-xs font-medium text-orange-700">Edge-native · real-time email trust</span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] ink">
                Email trust,<br />
                <span className="font-serif italic font-normal text-orange-600">simplified.</span>
              </h1>
              <p className="mt-7 text-lg sm:text-xl ink-soft leading-relaxed max-w-xl">
                See every sender using your domain. Detect spoofing before customers do. Enforce DMARC with confidence — from a single, beautifully simple platform.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/login" className="btn-accent" data-testid="hero-start-free">
                  Start free <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/pricing" className="btn-secondary" data-testid="hero-see-pricing">
                  See pricing
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-3 text-sm ink-muted">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span>No credit card · Verified-domain only · 5-minute setup</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <TelemetryHero />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST JOURNEY */}
      <section className="border-y border-stone-200 py-14 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center text-xs uppercase tracking-[0.2em] font-medium ink-muted mb-10">
            The TrustDmarc journey · from visibility to governance
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 rounded-2xl overflow-hidden">
            {[
              { n: "01", t: "DMARC visibility", d: "See every report, every sender, every alignment failure." },
              { n: "02", t: "Sender intelligence", d: "Classify SaaS, internal, suspicious — score every vendor." },
              { n: "03", t: "Operational enforcement", d: "Move safely from p=none to p=reject without breaking mail." },
              { n: "04", t: "Trust governance", d: "Continuous SSL, reputation, audit, and compliance posture." },
            ].map((s, i) => (
              <div key={s.n} className="bg-white p-7">
                <div className="flex items-center gap-2">
                  <div className="font-mono text-[10px] font-semibold text-orange-600 tracking-wider">STAGE {s.n}</div>
                  {i < 3 && <div className="h-px flex-1 bg-stone-200"></div>}
                </div>
                <div className="mt-4 font-display text-lg font-bold ink">{s.t}</div>
                <p className="mt-1.5 text-sm ink-soft leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION - centered */}
      <section className="py-32" data-testid="section-why">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">01 · Problem</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight ink leading-[1.05]">
            Modern email infrastructure<br />is <span className="font-serif italic font-normal">fragmented.</span>
          </h2>
          <p className="mt-7 ink-soft text-lg leading-relaxed">
            Every SaaS platform, marketing tool, helpdesk, and automation workflow introduces new sending infrastructure into your domain ecosystem. Most organizations lose visibility long before enforcement becomes safe.
          </p>
          <p className="mt-4 ink text-lg leading-relaxed font-medium">
            We continuously map, validate, and monitor your sending landscape — so you can move to p=reject without disrupting legitimate email.
          </p>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="py-32 bg-white border-y border-stone-200" data-testid="section-why-matters">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">Why it matters</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight ink leading-[1.05]">
              Email is the<br /><span className="font-serif italic font-normal">attack surface.</span>
            </h2>
            <p className="mt-6 ink-soft text-lg leading-relaxed">
              Authentication failures are silent. Spoofing is invisible until customers report it. Misconfigured SPF breaks legitimate mail without warning. Regulators are no longer asking — they're requiring.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { stat: "91%", label: "of cyberattacks start with email", src: "Deloitte" },
              { stat: "$2.9B", label: "lost to business email compromise in 2023", src: "FBI IC3" },
              { stat: "Required", label: "DMARC enforcement for bulk senders since Feb 2024", src: "Google + Yahoo" },
              { stat: "70%+", label: "of domains stay stuck at p=none indefinitely", src: "Industry analysis" },
            ].map((s) => (
              <div key={s.label} className="panel p-7">
                <div className="font-display text-4xl font-bold text-orange-600">{s.stat}</div>
                <p className="mt-3 ink leading-relaxed text-sm">{s.label}</p>
                <div className="mt-4 text-[10px] font-mono uppercase tracking-wider ink-muted">— {s.src}</div>
              </div>
            ))}
          </div>

          <div className="mt-14 max-w-3xl mx-auto">
            <div className="panel-tint p-6 flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-orange-600" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display font-bold ink">The cost of doing nothing is no longer "nothing."</div>
                <p className="mt-1.5 text-sm ink-soft leading-relaxed">
                  Brands without enforced DMARC are losing inbox placement, watching impersonation campaigns spread to customers, and failing compliance audits — often without realizing it for months.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO NEEDS IT */}
      <section className="py-32" data-testid="section-who">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <div className="text-xs font-semibold uppercase tracking-wider text-orange-600 mb-2">Who needs TrustDmarc</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight ink leading-[1.05]">
              Built for the teams<br /><span className="font-serif italic font-normal">running email at scale.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: ShieldCheck,
                role: "Security & SOC teams",
                jtbd: "Hunt BEC and impersonation campaigns. Get spoofing telemetry the moment it spikes — not after a customer complaint.",
              },
              {
                icon: Briefcase,
                role: "IT & email administrators",
                jtbd: "Untangle inherited SaaS sprawl. Fix SPF lookup explosions. Move to p=reject without breaking the marketing team's email.",
              },
              {
                icon: Building2,
                role: "MSPs & MSSPs",
                jtbd: "Manage email trust across dozens of customer domains from one multi-tenant console. White-label reporting included.",
              },
              {
                icon: Users,
                role: "Compliance & GRC",
                jtbd: "Maintain audit-ready DMARC posture. Export evidence on demand for SOC 2, PCI-DSS, HIPAA, and FedRAMP reviews.",
              },
              {
                icon: TrendingUp,
                role: "Marketing & deliverability",
                jtbd: "Protect inbox placement. Validate every new sending vendor. Catch reputation drift before campaigns fail.",
              },
              {
                icon: ShoppingBag,
                role: "E-commerce & B2C brands",
                jtbd: "Stop phishing impersonation of order confirmations and password resets. Light up your BIMI logo when ready.",
              },
              {
                icon: Landmark,
                role: "Finance & banking",
                jtbd: "Meet regulator mandates (FFIEC, PCI). Detect wire-fraud spoofing attempts. Tighten enforcement with confidence.",
              },
              {
                icon: Stethoscope,
                role: "Healthcare & life sciences",
                jtbd: "Protect patient communications. Satisfy HIPAA email-integrity controls. Monitor every PHI-handling vendor's sending posture.",
              },
              {
                icon: Globe,
                role: "Government & public sector",
                jtbd: "BOD 18-01 / 22-01 DMARC reject enforcement. Continuous proof of control for every owned domain.",
              },
            ].map(({ icon: Icon, role, jtbd }) => (
              <div key={role} className="panel p-6">
                <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-orange-600" strokeWidth={2} />
                </div>
                <div className="font-display font-bold ink">{role}</div>
                <p className="mt-2 text-sm ink-soft leading-relaxed">{jtbd}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MONITOR GRID */}
      <section className="py-24 bg-white border-y border-stone-200" data-testid="section-monitor">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">02 · Surface area</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight ink leading-[1.05]">
              Ten telemetry surfaces.<br />One trust layer.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {monitorCards.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="panel p-6 hover:border-stone-300 transition-colors" data-testid={`monitor-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-orange-600" strokeWidth={2} />
                </div>
                <div className="font-display font-bold ink">{label}</div>
                <p className="mt-2 text-sm ink-soft leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PIPELINE - centered narrative */}
      <section className="py-32" data-testid="section-pipeline">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">03 · Detection pipeline</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight ink leading-[1.05]">
            From inbox to <span className="font-serif italic font-normal">enforcement</span> in seconds.
          </h2>
          <p className="mt-6 ink-soft text-lg">
            Every report flows through five stages. None are skipped.
          </p>
        </div>
        <div className="max-w-6xl mx-auto px-6 mt-16">
          <div className="grid md:grid-cols-5 gap-3">
            {pipeline.map((p, i) => (
              <div key={p.label} className="panel p-5 relative">
                <div className="text-xs font-mono ink-muted mb-2">step {String(i + 1).padStart(2, "0")}</div>
                <div className="font-display font-bold ink">{p.label}</div>
                <p className="mt-2 text-xs ink-soft leading-relaxed">{p.desc}</p>
                {i < pipeline.length - 1 && (
                  <div className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-5 w-5 rounded-full bg-white border border-stone-200 items-center justify-center">
                    <ArrowRight className="h-3 w-3 text-orange-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERIFICATION */}
      <section className="py-32 bg-white border-y border-stone-200" data-testid="section-verification">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">04 · Trust foundation</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight ink leading-[1.05]">
              Trust begins with <span className="font-serif italic font-normal">verified ownership.</span>
            </h2>
            <p className="mt-6 ink-soft text-lg leading-relaxed">
              Every monitored domain completes cryptographic ownership verification before telemetry collection, analysis, or enforcement guidance is enabled.
            </p>
          </div>

          <div className="panel-shadow p-2 max-w-4xl mx-auto">
            <div className="bg-[#0F0E0C] rounded-lg p-6 font-mono text-sm overflow-x-auto">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-stone-700"></div>
                  <div className="h-2.5 w-2.5 rounded-full bg-stone-700"></div>
                  <div className="h-2.5 w-2.5 rounded-full bg-stone-700"></div>
                </div>
                <div className="text-stone-500 text-xs ml-2">_trustdmarc.example.com · TXT</div>
              </div>
              <div className="text-stone-400">$ dig _trustdmarc.example.com TXT +short</div>
              <div className="text-orange-300 mt-1">"trustdmarc-verification=a8f2c91be4d7e2a9b04f"</div>
              <div className="text-emerald-400 mt-3">[edge-worker]   resolved at lhr1 (4ms)</div>
              <div className="text-emerald-400">[validator]     token match: ok</div>
              <div className="text-emerald-400">[tenant-map]    bound to org_4a2c</div>
              <div className="text-emerald-400">[ingestion]     enabled at 2026-02-18T11:42:08Z</div>
            </div>
            <div className="grid grid-cols-4 gap-1 p-2 pt-3">
              {[
                ["Pending", true], ["DNS Detected", true], ["Verified", true], ["Telemetry On", false],
              ].map(([s, done], i) => (
                <div
                  key={s}
                  className={`text-center py-2.5 rounded-md text-xs font-medium ${
                    done ? "bg-orange-50 text-orange-700" : "bg-stone-100 text-stone-500"
                  }`}
                >
                  {done ? "✓ " : ""}{s}
                </div>
              ))}
            </div>
          </div>

          <p className="text-center mt-10 font-serif italic text-xl ink-soft">
            Only verified domains. No blind trust. No unauthorized telemetry.
          </p>
        </div>
      </section>

      {/* INFRA */}
      <section className="py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">05 · Infrastructure</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight ink leading-[1.05]">
              Built for modern infrastructure.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { t: "Edge ingestion", d: "Globally distributed workers process DMARC reports at the edge — sub-second routing." },
              { t: "Async pipelines", d: "Queue-backed processors decouple ingestion from analysis. Telemetry never blocks." },
              { t: "Multi-tenant by design", d: "Strict tenant isolation enforced at routing, storage, and API boundaries." },
            ].map((x) => (
              <div key={x.t} className="panel p-7">
                <div className="font-display text-xl font-bold ink">{x.t}</div>
                <p className="mt-3 text-sm ink-soft leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="py-32 bg-white border-y border-stone-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">06 · Pricing</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight ink leading-[1.05]">
              From visibility to enforcement.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { tier: "Free", price: "$0", features: ["1 domain", "30-day retention", "Email alerts"] },
              { tier: "Pro", price: "$49", features: ["10 domains", "12-month retention", "Slack + Teams", "API access"], featured: true },
              { tier: "Enterprise", price: "Custom", features: ["Unlimited domains", "MSP multi-tenant", "SSO + RBAC", "SLA"] },
            ].map((p) => (
              <div key={p.tier} className={`panel p-7 ${p.featured ? "ring-2 ring-orange-500 ring-offset-2 ring-offset-white" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display text-xl font-bold ink">{p.tier}</div>
                  {p.featured && <span className="chip chip-accent">Most adopted</span>}
                </div>
                <div className="mt-5 flex items-baseline gap-1">
                  <div className="font-display text-4xl font-bold ink">{p.price}</div>
                  {p.price !== "Custom" && <div className="ink-soft text-sm">/mo</div>}
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm ink-soft">
                      <CheckCircle2 className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/pricing" className="btn-secondary" data-testid="see-full-pricing">
              See full pricing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-5xl lg:text-6xl font-bold tracking-tight ink leading-[1.02]">
            Move from monitoring<br />to <span className="font-serif italic font-normal text-orange-600">enforcement.</span>
          </h2>
          <p className="mt-7 ink-soft text-lg max-w-xl mx-auto">
            Onboard a domain in under five minutes. Receive your first sender map within an hour.
          </p>
          <div className="mt-10 flex justify-center gap-3">
            <Link to="/login" className="btn-accent" data-testid="cta-start-free">
              Start free <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/developers" className="btn-secondary" data-testid="cta-developers">
              Explore the API
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
